/**
 * CoachDashboardPage - Dashboard for coaches to manage their clients
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Home,
  Users,
  Bell,
  Settings,
  Check,
  X,
  Dumbbell,
  Calendar,
  TrendingUp,
  Loader2,
  UserCheck,
  Clock,
} from 'lucide-react';
import { useCoachStore, COACH_SPECIALIZATIONS } from '../../stores/coachStore';
import { formatDistanceToNow } from 'date-fns';
import { tr } from 'date-fns/locale';

const CoachDashboardPage = () => {
  const navigate = useNavigate();
  const {
    coachProfileData,
    hasCoachProfile,
    clients,
    isLoadingClients,
    pendingInvitations,
    isLoadingInvitations,
    loadClients,
    loadPendingInvitations,
    acceptInvitation,
    rejectInvitation,
    isActingAsCoach,
    isLoadingCoachProfile,
  } = useCoachStore();

  useEffect(() => {
    if (isActingAsCoach) {
      loadClients();
      loadPendingInvitations();
    }
  }, [isActingAsCoach, loadClients, loadPendingInvitations]);

  // Redirect if not acting as coach (no clients or pending invitations)
  if (!isLoadingCoachProfile && !isActingAsCoach) {
    navigate('/become-coach');
    return null;
  }

  const handleAccept = async (relationshipId) => {
    try {
      await acceptInvitation(relationshipId);
    } catch (error) {
      // Error handled in store
    }
  };

  const handleReject = async (relationshipId) => {
    if (confirm('Bu daveti reddetmek istediğinize emin misiniz?')) {
      try {
        await rejectInvitation(relationshipId);
      } catch (error) {
        // Error handled in store
      }
    }
  };

  if (isLoadingCoachProfile) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-gray-950/80 backdrop-blur-xl border-b border-gray-800">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
              >
                <Home className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2">
                  <Dumbbell className="w-6 h-6 text-orange-500" />
                  Koç Paneli
                </h1>
                <p className="text-sm text-gray-400">
                  {hasCoachProfile ? (
                    <>Hoş geldin, {coachProfileData?.display_name}</>
                  ) : (
                    <>Öğrencilerinizi yönetin</>
                  )}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {pendingInvitations.length > 0 && (
                <div className="relative">
                  <Bell className="w-5 h-5 text-orange-400" />
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-orange-500 text-white text-xs rounded-full flex items-center justify-center">
                    {pendingInvitations.length}
                  </span>
                </div>
              )}
              <button className="p-2 hover:bg-gray-800 rounded-lg transition-colors">
                <Settings className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-6 space-y-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            icon={<Users className="w-5 h-5" />}
            label="Aktif Öğrenci"
            value={clients.length}
            color="blue"
          />
          <StatCard
            icon={<Bell className="w-5 h-5" />}
            label="Bekleyen Davet"
            value={pendingInvitations.length}
            color="yellow"
          />
          <StatCard
            icon={<Calendar className="w-5 h-5" />}
            label="Bu Hafta"
            value="-"
            color="green"
          />
          <StatCard
            icon={<TrendingUp className="w-5 h-5" />}
            label="Toplam Not"
            value="-"
            color="purple"
          />
        </div>

        {/* Pending Invitations */}
        {pendingInvitations.length > 0 && (
          <section>
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-yellow-400" />
              Bekleyen Davetler
              <span className="text-sm text-gray-400">({pendingInvitations.length})</span>
            </h2>

            <div className="bg-gray-900/50 border border-gray-800 rounded-xl overflow-hidden">
              {isLoadingInvitations ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
                </div>
              ) : (
                <div className="divide-y divide-gray-800">
                  {pendingInvitations.map((invitation) => {
                    const inviterName = invitation.client?.display_name || invitation.client?.email?.split('@')[0] || 'Kullanıcı';
                    const inviterAvatar = invitation.client?.avatar_url;

                    return (
                    <div
                      key={invitation.id}
                      className="p-4 flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        {inviterAvatar ? (
                          <img
                            src={inviterAvatar}
                            alt={inviterName}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-yellow-500/10 rounded-full flex items-center justify-center">
                            <span className="text-sm font-semibold text-yellow-400">
                              {inviterName.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                        <div>
                          <p className="font-medium">
                            {inviterName}
                          </p>
                          <p className="text-sm text-gray-400">
                            {formatDistanceToNow(new Date(invitation.invited_at), {
                              addSuffix: true,
                              locale: tr,
                            })}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <div className="text-xs text-gray-500 mr-4">
                          {invitation.can_view_workouts && (
                            <span className="inline-block mr-2">Antrenmanlar</span>
                          )}
                          {invitation.can_view_analytics && (
                            <span className="inline-block">Analizler</span>
                          )}
                        </div>
                        <motion.button
                          onClick={() => handleAccept(invitation.id)}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="flex items-center gap-1 px-3 py-1.5 bg-green-500/10 hover:bg-green-500/20 text-green-400 text-sm rounded-lg transition-colors"
                        >
                          <Check className="w-4 h-4" />
                          Kabul Et
                        </motion.button>
                        <motion.button
                          onClick={() => handleReject(invitation.id)}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="flex items-center gap-1 px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-sm rounded-lg transition-colors"
                        >
                          <X className="w-4 h-4" />
                          Reddet
                        </motion.button>
                      </div>
                    </div>
                  );
                  })}
                </div>
              )}
            </div>
          </section>
        )}

        {/* Active Clients */}
        <section>
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-400" />
            Aktif Öğrenciler
            {clients.length > 0 && (
              <span className="text-sm text-gray-400">({clients.length})</span>
            )}
          </h2>

          {isLoadingClients ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
            </div>
          ) : clients.length === 0 ? (
            <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-12 text-center">
              <Users className="w-16 h-16 text-gray-700 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-400 mb-2">
                Henüz öğrenciniz yok
              </h3>
              <p className="text-gray-500 max-w-md mx-auto">
                Öğrenciler sizi davet ettiğinde burada görünecekler.
                {!hasCoachProfile && (
                  <button
                    onClick={() => navigate('/become-coach')}
                    className="block mt-3 mx-auto text-orange-400 hover:text-orange-300 transition-colors"
                  >
                    Koç profilinizi oluşturmak için tıklayın
                  </button>
                )}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {clients.map((client) => (
                <ClientCard
                  key={client.id}
                  client={client}
                  onClick={() => navigate(`/fittrack?client=${client.client_id}`)}
                />
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon, label, value, color }) => {
  const colorClasses = {
    blue: 'bg-blue-500/10 text-blue-400',
    yellow: 'bg-yellow-500/10 text-yellow-400',
    green: 'bg-green-500/10 text-green-400',
    purple: 'bg-purple-500/10 text-purple-400',
  };

  return (
    <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${colorClasses[color]}`}>
        {icon}
      </div>
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-sm text-gray-400">{label}</p>
    </div>
  );
};

// Client Card Component
const ClientCard = ({ client, onClick }) => {
  const clientName = client.client?.display_name || client.client?.email?.split('@')[0] || 'Öğrenci';
  const avatarUrl = client.client?.avatar_url;

  return (
    <motion.button
      onClick={onClick}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className="bg-gray-900/50 border border-gray-800 hover:border-gray-700 rounded-xl p-5 text-left transition-all w-full"
    >
      <div className="flex items-start gap-4">
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt={clientName}
            className="w-12 h-12 rounded-full object-cover flex-shrink-0"
          />
        ) : (
          <div className="w-12 h-12 bg-blue-500/10 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-lg font-semibold text-blue-400">
              {clientName.charAt(0).toUpperCase()}
            </span>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-semibold truncate">
            {clientName}
          </p>
          <p className="text-sm text-gray-400 truncate">
            {client.client?.email}
          </p>
        </div>
      </div>

      {/* Permissions */}
      <div className="mt-4 flex flex-wrap gap-2">
        {client.can_view_workouts && (
          <span className="px-2 py-1 bg-gray-800 text-gray-300 text-xs rounded">
            Antrenmanlar
          </span>
        )}
        {client.can_view_analytics && (
          <span className="px-2 py-1 bg-gray-800 text-gray-300 text-xs rounded">
            Analizler
          </span>
        )}
      </div>

      {/* Last Activity */}
      <div className="mt-4 pt-3 border-t border-gray-800 flex items-center justify-between text-xs text-gray-500">
        <span>
          Kabul:{' '}
          {new Date(client.accepted_at).toLocaleDateString('tr-TR', {
            day: 'numeric',
            month: 'short',
          })}
        </span>
        <span className="text-orange-400">Görüntüle</span>
      </div>
    </motion.button>
  );
};

export default CoachDashboardPage;
